/*
 * Copyright (c) 2022 Promontory Interfinancial Network, LLC
 */

export class BankCustomizationUtils {
    static readonly OPACITY = '1A';
    static readonly OPACITY_TAB_SHADOW = '40';
    static readonly OPACITY_CARD_SHADOW = '1F';
    static readonly DEFAULT_COLOR = '#0063E5'; // _palette.$color-action-primary ($primitive-color-palette-blue-60)
    static readonly HEX_REG = /^#[0-9A-F]{6}$/i;
    static readonly ACTIVE_TAB_LABEL_COLOR ='#0063E5';   // _palette.$color-action-primary ($primitive-color-palette-blue-60)
    static readonly INACTIVE_TAB_LABEL_COLOR ='#454A54'; // _palette.$$color-content-subtle ($primitive-color-palette-neutral-80)
    static readonly CONTENT_INVERSE_COLOR = '#FFFFFF';   // _palette.$color-content-inverse
    static readonly CONTENT_DEFAULT_COLOR = '#1C1E22';   // _palette.$color-content-default
    static readonly MIN_CONTRAST_RATIO = 4.5;            // WCAG 2.1 AA for normal-size text

    static changeBankCustomizationColor(colorCode: string) {
        if (!this.HEX_REG.test(colorCode)) {
            return;
        }

        colorCode = colorCode.toUpperCase();
        const isDefaultColor = colorCode === this.DEFAULT_COLOR;

        const bankCustomizationColor = colorCode;
        const bankCustomizationActiveTabLabelColor = isDefaultColor ? this.DEFAULT_COLOR : this.ACTIVE_TAB_LABEL_COLOR;
        const bankCustomizationInactiveTabLabelColor = isDefaultColor ? this.DEFAULT_COLOR : this.INACTIVE_TAB_LABEL_COLOR;
        const bankCustomizationTransparentColor = colorCode + this.OPACITY;
        const bankCustomizationTransparentTabShadowColor = colorCode + this.OPACITY_TAB_SHADOW;
        const bankCustomizationTransparentCardShadowColor = colorCode + this.OPACITY_CARD_SHADOW;
        const bankCustomizationCardBorderColor = colorCode;

        document.documentElement.style.setProperty('--bank-customization-color', bankCustomizationColor);
        document.documentElement.style.setProperty('--bank-customization-active-tab-label-color', bankCustomizationActiveTabLabelColor);
        document.documentElement.style.setProperty('--bank-customization-inactive-tab-label-color', bankCustomizationInactiveTabLabelColor);
        document.documentElement.style.setProperty('--bank-customization-transparent-color', bankCustomizationTransparentColor);
        document.documentElement.style.setProperty('--bank-customization-transparent-tab-shadow-color', bankCustomizationTransparentTabShadowColor);
        document.documentElement.style.setProperty('--bank-customization-transparent-card-shadow-color', bankCustomizationTransparentCardShadowColor);
        document.documentElement.style.setProperty('--bank-customization-transparent-card-border-color', bankCustomizationCardBorderColor);
        document.documentElement.style.setProperty('--bank-customization-content-color', this.getReadableContentColor(colorCode));
        this.applyCardBackground(colorCode);
    }

    private static applyCardBackground(colorCode: string) {
        if (colorCode === this.DEFAULT_COLOR) {
            document.documentElement.style.removeProperty('--bank-customization-card-background');
            return;
        }

        document.documentElement.style.setProperty('--bank-customization-card-background', colorCode);
    }

    static resetBankCustomizationColor() {
        this.changeBankCustomizationColor(this.DEFAULT_COLOR);
    }

    /**
     * Returns the text color to use on top of the given background color: the inverse (white) content color when it
     * meets the WCAG AA contrast threshold, otherwise the default (near black) content color.
     */
    static getReadableContentColor(backgroundColorCode: string): string {
        if (!this.HEX_REG.test(backgroundColorCode)) {
            return this.CONTENT_INVERSE_COLOR;
        }

        return this.getContrastRatio(backgroundColorCode, this.CONTENT_INVERSE_COLOR) >= this.MIN_CONTRAST_RATIO
            ? this.CONTENT_INVERSE_COLOR
            : this.CONTENT_DEFAULT_COLOR;
    }

    private static getContrastRatio(firstColorCode: string, secondColorCode: string): number {
        const firstLuminance = this.getRelativeLuminance(firstColorCode);
        const secondLuminance = this.getRelativeLuminance(secondColorCode);

        const lighter = Math.max(firstLuminance, secondLuminance);
        const darker = Math.min(firstLuminance, secondLuminance);

        return (lighter + 0.05) / (darker + 0.05);
    }

    private static getRelativeLuminance(colorCode: string): number {
        const [red, green, blue] = [1, 3, 5]
            .map(offset => Number.parseInt(colorCode.substring(offset, offset + 2), 16) / 255)
            .map(channel => channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4));

        return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    }
}
